import { Agent } from '../libs/agent'
import { ISaleProgram, ISaleProgramFilter } from '../model/sale-program.model'

export interface ISaleProgramFilterExtended extends ISaleProgramFilter {
  title?: string // Thêm filter theo title
}

export class SaleProgramService {
  private agent: Agent

  constructor(agent: Agent) {
    this.agent = agent
  }

  /**
   * Lấy danh sách sale programs đang active với filter
   * @param filter - Các điều kiện lọc
   * @returns Danh sách sale programs
   */
  async getActivePrograms(filter?: ISaleProgramFilterExtended): Promise<ISaleProgram[]> {
    const params = new URLSearchParams()

    if (filter && filter.outlet) {
      params.append('outlet', filter.outlet)
    }

    if (filter && filter.type) {
      params.append('type', filter.type)
    }

    if (filter && filter.target) {
      params.append('target', filter.target)
    }

    if (filter && filter.title) {
      params.append('title', filter.title)
    }

    if (filter && filter.date) {
      const dateStr = typeof filter.date === 'string' ? filter.date : filter.date.toISOString()
      params.append('date', dateStr)
    }

    const queryString = params.toString()
    const endpoint = queryString
      ? `/api_savor/sale-programs/active?${queryString}`
      : '/api_savor/sale-programs/active'

    return this.agent.get<ISaleProgram[]>(endpoint)
  }

  /**
   * Lấy sale programs cho bánh trung thu (không cần outlet)
   * @param date - Ngày áp dụng
   * @returns Danh sách programs bánh trung thu
   */
  async getMooncakePrograms(date?: Date): Promise<ISaleProgram[]> {
    return this.getActivePrograms({
      title: 'trung thu', // Regex sẽ match trong backend
      type: 'Giá sỉ cho nhóm sản phẩm'
    })
  }

  /**
   * Kiểm tra và tính giảm giá sỉ cho sản phẩm
   * @param programs - Danh sách sale programs
   * @param sku - SKU của sản phẩm
   * @param quantity - Số lượng
   * @param category - Category của sản phẩm
   * @returns Thông tin giảm giá
   */
  calculateWholesaleDiscount(
    programs: ISaleProgram[],
    sku: string,
    quantity: number,
    category?: string
  ): {
    discountPercent: number
    saleProgram: ISaleProgram | null
    bundlePrice: any | null
  } {
    for (const program of programs) {
      // Kiểm tra sản phẩm có trong danh sách không
      const productInProgram = program.products.find(
        p => p.sku === sku || (p.product && p.product.sku === sku)
      )

      if (!productInProgram) {
        continue
      }

      // Nếu có isWholesale flag
      if (productInProgram.isWholesale && program.bundlePrices.length > 0) {
        // Tìm mốc giảm giá phù hợp với số lượng
        const validBundlePrices = program.bundlePrices
          .filter(bp => quantity >= bp.quantity)
          .sort((a, b) => b.quantity - a.quantity)

        const bundlePrice = validBundlePrices[0]

        if (bundlePrice) {
          return {
            discountPercent: bundlePrice.percent || 0,
            saleProgram: program,
            bundlePrice
          }
        }
      }

      // Nếu có salePrice cố định
      if (productInProgram.salePrice) {
        return {
          discountPercent: 0, // Sẽ tính sau dựa trên giá gốc
          saleProgram: program,
          bundlePrice: null
        }
      }
    }

    return {
      discountPercent: 0,
      saleProgram: null,
      bundlePrice: null
    }
  }
}

// Export singleton instance
export const saleProgramService = (agent: Agent) => new SaleProgramService(agent)
